/**
 * @file planHistoryGroups.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description How Plan History is laid out (Cam, 30 Oct 2026: "tabs not
 *              filters: Emails / Order Amendments / Planning … then group by
 *              date, today's top, rest collapsed by default, and sub group by
 *              event type and order by time, all subgroups collapsed by
 *              default"). Pure — no clock: the caller says which day is today.
 *
 *   TABS — Emails (kind 'email'), Order Amendments ('order'), Planning
 *   ('plan'), in that order; Emails first.
 *
 *   DAYS — one group per London calendar day an event happened, newest first.
 *
 *   SUBGROUPS — one action each, newest first by their LATEST event, headed
 *   with the time of their first:
 *     - an email the server gave a `send` key (one driver paperwork or mill
 *       send, one office summary) is grouped with the rest of its send;
 *     - anything else — PO emails and every other email with no send key
 *       included — is a RUN: an event joins the newest run of its own TYPE by
 *       the same user whose latest event is within RUN_GAP_MS, even when
 *       other types' events came in between (1.1.0 — typing a collection time
 *       writes TIME_SET and then DROP_TIME_SET in the same second; they made
 *       alternating single groups); else it starts a run. So one bulk board
 *       change of ten lines, or five POs emailed in a row, is one subgroup.
 *   The TYPE (typeKeyOf) is the event's kind of change without the parts
 *   about one record: a date move off / onto the day; an edit by the fields it
 *   changed (UPDATE, PO_UPDATE and LINE_UPDATE alike, underscore context
 *   fields left out — an order line's and its PO line's "Haulier changed" from
 *   one board save are one type); an email by its kind; else the audit action.
 */

import { isDeliveryProblem } from './emailDelivery';

export const PLAN_HISTORY_TABS = [
  { key: 'email', label: 'Emails', icon: 'mail', unit: ['email', 'emails'] },
  { key: 'order', label: 'Order Amendments', icon: 'receipt_long', unit: ['change', 'changes'] },
  { key: 'plan', label: 'Planning', icon: 'route', unit: ['change', 'changes'] }
];

/** Two events further apart than this are two actions, not one. */
export const RUN_GAP_MS = 2 * 60 * 1000;

const EDIT_ACTIONS = new Set(['UPDATE', 'PO_UPDATE', 'LINE_UPDATE']);

const LONDON_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit'
});

const timeOf = (ev) => {
  const t = new Date(ev?.at).getTime();
  return Number.isFinite(t) ? t : 0;
};

/** The London calendar day ('YYYY-MM-DD') a moment fell on; '' when it is no moment. */
export function londonDay(at) {
  const d = new Date(at);
  if (at == null || Number.isNaN(d.getTime())) return '';
  return LONDON_DAY.format(d);
}

const LONG_DAY_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
});

/**
 * "Monday 2 November 2026" for 'YYYY-MM-DD' — built from its parts, since
 * some ICU versions put a comma after the weekday and some do not.
 */
export function longDayLabel(day) {
  const d = new Date(`${day}T12:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day || '')) || Number.isNaN(d.getTime())) return String(day || '');
  const p = Object.fromEntries(LONG_DAY_PARTS.formatToParts(d).map(x => [x.type, x.value]));
  return `${p.weekday} ${p.day} ${p.month} ${p.year}`;
}

/** An email that bounced, was marked as spam, was rejected or failed to send. */
export function eventHasProblem(ev) {
  return ev?.kind === 'email' && isDeliveryProblem(ev?.email?.status);
}

/** Where a date move took an order, relative to the day being viewed: 'off' | 'on' | null. */
export function dateMoveOf(ev, date) {
  const move = ev?.changes?.delivery_date;
  if (!move || typeof move !== 'object' || !date) return null;
  const from = String(move.old || '').slice(0, 10);
  const to = String(move.new || '').slice(0, 10);
  if (from === date && to !== date) return 'off';
  if (to === date && from !== date) return 'on';
  return null;
}

/** The fields an edit changed, without the underscore context ones, sorted. */
export function changedFields(ev) {
  return Object.keys(ev?.changes || {}).filter(k => !k.startsWith('_')).sort();
}

/** An event's type: what was done, without which record it was done to. */
export function typeKeyOf(ev, date) {
  if (ev?.kind === 'email' || ev?.action === 'EMAIL_SENT') {
    return `email:${ev?.email?.label || ev?.emailLabel || ''}`;
  }
  const move = dateMoveOf(ev, date);
  if (move) return `move:${move}`;
  if (EDIT_ACTIONS.has(ev?.action)) {
    const fields = changedFields(ev);
    if (fields.length) return `edit:${fields.join(',')}`;
  }
  return `action:${ev?.action || ''}`;
}

/** Newest first; at the same moment, the later-listed first (stable). */
const newestFirst = (a, b) => timeOf(b) - timeOf(a);

function finish(group) {
  group.events.sort(newestFirst);
  const times = group.events.map(timeOf);
  group.startAt = group.events[group.events.length - 1]?.at ?? null;
  group.endAt = group.events[0]?.at ?? null;
  group.start = Math.min(...times);
  group.end = Math.max(...times);
  group.count = group.events.length;
  group.users = [...new Set(group.events.map(e => e.user).filter(Boolean))];
  group.user = group.users[0] || null;
  group.problems = group.events.filter(eventHasProblem).length;
  group.problem = group.problems > 0;
  return group;
}

/**
 * One day's events in subgroups (see the header), newest first by their latest
 * event. Each: { key, typeKey, send, sample (its first event, for words),
 * events (newest first), startAt (its first event's time — the heading's),
 * endAt (its latest), count, user, users, problems, problem }.
 */
export function subgroupsOf(events, date) {
  const asc = [...(events || [])].sort((a, b) => timeOf(a) - timeOf(b));
  const groups = [];
  const bySend = new Map();
  // The newest run of each type and user: `${type}\u0000${user}` → run.
  const runs = new Map();
  for (const ev of asc) {
    if (ev.send) {
      let g = bySend.get(ev.send);
      if (!g) {
        g = { key: `send:${ev.send}`, typeKey: typeKeyOf(ev, date), send: ev.send, sample: ev, events: [], order: groups.length };
        bySend.set(ev.send, g);
        groups.push(g);
      }
      g.events.push(ev);
      continue;
    }
    const type = typeKeyOf(ev, date);
    const t = timeOf(ev);
    const runKey = `${type}\u0000${ev.user || ''}`;
    const run = runs.get(runKey);
    if (run && t - run.lastAt <= RUN_GAP_MS) {
      run.events.push(ev);
      run.lastAt = t;
    } else {
      const fresh = { key: `run:${ev.key}`, typeKey: type, send: null, sample: ev, events: [ev], lastAt: t, order: groups.length };
      runs.set(runKey, fresh);
      groups.push(fresh);
    }
  }
  return groups.map(g => {
    const { lastAt, ...rest } = g;
    return finish(rest);
  }).sort((a, b) => (b.end - a.end) || (b.order - a.order)).map(({ order, ...g }) => g);
}

/**
 * A tab's events in days, newest first: [{ day, label, events, count,
 * problems, problem, subgroups }]. `date` is the delivery day being viewed
 * (date moves read relative to it).
 */
export function dayGroupsOf(events, { date } = {}) {
  const byDay = new Map();
  for (const ev of events || []) {
    const day = londonDay(ev.at);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(ev);
  }
  return [...byDay.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([day, evs]) => {
      const sorted = [...evs].sort(newestFirst);
      const problems = sorted.filter(eventHasProblem).length;
      return {
        day,
        label: longDayLabel(day),
        events: sorted,
        count: sorted.length,
        problems,
        problem: problems > 0,
        subgroups: subgroupsOf(sorted, date)
      };
    });
}

/** How many events each tab holds, and how many of its emails did not arrive. */
export function tabCounts(events) {
  const out = { email: 0, order: 0, plan: 0, emailProblems: 0 };
  for (const ev of events || []) {
    if (ev.kind in out) out[ev.kind] += 1;
    if (eventHasProblem(ev)) out.emailProblems += 1;
  }
  return out;
}

/** "4 emails", "1 change". */
export function countWords(n, tabKey) {
  const tab = PLAN_HISTORY_TABS.find(t => t.key === tabKey) || PLAN_HISTORY_TABS[2];
  return `${n} ${n === 1 ? tab.unit[0] : tab.unit[1]}`;
}

/*
 * Open or closed. A day opens by default when it is today (or a search is
 * typed), a subgroup only when a search is typed; `flipped` says the person
 * clicked it away from its default.
 */
export function isDayOpen({ isToday = false, searching = false, flipped = false } = {}) {
  const byDefault = !!(searching || isToday);
  return flipped ? !byDefault : byDefault;
}

export function isSubgroupOpen({ searching = false, flipped = false } = {}) {
  return flipped ? !searching : !!searching;
}
